"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Building2, Edit, Trash2, RefreshCw, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StructureTab from "@/components/ClientDetail/StructureTab";
import CreateFilialDrawer from "@/components/ClientDetail/CreateFilialDrawer";
import GrupoEconomicoTab from "@/components/ClientDetail/GrupoEconomicoTab";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { maskCNPJ, maskCEP, maskPhone, maskCNAE } from "@/lib/masks";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { z } from "zod";
import DevBanner from "@/components/ClientDetail/DevBanner";
import CompanyStatusBadges from "@/components/ClientDetail/CompanyStatusBadges";

const ESTADOS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

const GRAUS_RISCO = [
  { value: "1", label: "Grau 1 - Risco Mínimo" },
  { value: "2", label: "Grau 2 - Risco Baixo" },
  { value: "3", label: "Grau 3 - Risco Médio" },
  { value: "4", label: "Grau 4 - Risco Alto" },
];

// Schema Zod para validação frontend (mesmo do backend)
const clientFormSchema = z.object({
  channelId: z.number().int().positive("Canal é obrigatório"),
  nomeFantasia: z.string().min(2, "Nome Fantasia deve ter pelo menos 2 caracteres").trim(),
  razaoSocial: z.string().min(2, "Razão Social deve ter pelo menos 2 caracteres").trim(),
  cnpj: z.string().min(14, "CNPJ deve conter 14 dígitos"),
  
  // Endereço (obrigatório)
  logradouro: z.string().min(2, "Logradouro deve ter pelo menos 2 caracteres").trim(),
  numero: z.string().min(1, "Número é obrigatório").trim(),
  complemento: z.string().trim().optional().nullable().or(z.literal("")).transform(v => v === "" ? null : v), // V84: aceita vazio e números
  bairro: z.string().min(2, "Bairro deve ter pelo menos 2 caracteres").trim(),
  cidade: z.string().min(2, "Cidade deve ter pelo menos 2 caracteres").trim(),
  estado: z.string().regex(/^[A-Z]{2}$/, "Estado deve ser 2 letras maiúsculas (ex: SP)"),
  cep: z.string().regex(/^\d{8}$/, "CEP deve conter 8 dígitos"),
  
  // Dados NR1 (opcionais)
  cnae: z.union([z.literal(""), z.string().regex(/^\d{7}$/, "CNAE deve conter 7 dígitos")]).optional(),
  grauRisco: z.string().optional().or(z.literal("")),
  atividadePrincipal: z.string().optional().or(z.literal("")),
  sector: z.string().optional().or(z.literal("")),
  employeesCount: z.number().int().nonnegative().optional().or(z.literal(0)),
  
  // Contato (opcionais)
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  responsibleName: z.string().trim().optional().or(z.literal("")),
});

type ClientFormData = z.infer<typeof clientFormSchema>;

interface FieldErrors {
  [key: string]: string;
}

interface BackendErrorResponse {
  ok: false;
  error: {
    fieldErrors?: FieldErrors;
    formError?: string;
    code?: string;
  };
}

export default function Clients() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'structure' | 'group'>('overview');
  const [simulatedIsMatriz, setSimulatedIsMatriz] = useState(false);
  const { user } = useAuth();

  const [formData, setFormData] = useState<Partial<ClientFormData>>({
    channelId: 0,
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    cnae: "",
    atividadePrincipal: "",
    grauRisco: "",
    sector: "",
    employeesCount: 0,
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    email: "",
    phone: "",
    responsibleName: "",
  });

  const isChannelAdmin = user?.role === "channel_admin" || user?.role === "channel_admin_multi";
  const isAdminSalvia = user?.role === "admin";

  const { data: channels } = trpc.channels.list.useQuery(undefined, {
    enabled: isAdminSalvia,
  });
  
  // V89: Passar status para o backend (não filtrar client-side sobre lista já filtrada)
  const listQueryInput = {
    ...(selectedChannelId && selectedChannelId !== "0" ? { channelId: parseInt(selectedChannelId) } : {}),
    status: statusFilter,
  };

  const { data: listResponse, isLoading, refetch } = trpc.clients.list.useQuery(
    listQueryInput,
    { staleTime: 0 } // sempre recarregar ao mudar filtro
  );

  // Extrair items da resposta estruturada
  const allClients = listResponse?.items || [];

  // Filtrar e buscar clientes (apenas busca textual — status já vem do backend)
  const clients = useMemo(() => {
    let filtered = allClients || [];
    
    // Buscar por nome, razão social ou CNPJ
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(client => 
        (client.nomeFantasia?.toLowerCase().includes(term)) ||
        (client.razaoSocial?.toLowerCase().includes(term)) ||
        (client.cnpj?.includes(term))
      );
    }
    
    return filtered;
  }, [allClients, searchTerm]);

  /**
   * Consumir error contract estruturado do backend
   */
  const createClient = trpc.clients.create.useMutation({
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success("✅ Cliente criado com sucesso!");
        setIsDialogOpen(false);
        resetForm();
        refetch();
      }
    },
    onError: (error: any) => {
      // Tentar extrair error contract estruturado
      const errorData = error?.data?.zodError || error?.data;
      
      if (Array.isArray(errorData)) {
        // ZodError do backend
        const errors: FieldErrors = {};
        errorData.forEach((issue: any) => {
          const field = issue.path?.[0] || "form";
          errors[field] = issue.message || "Erro de validação";
        });
        setFieldErrors(errors);
        setFormError("Revise os campos destacados para continuar.");
        toast.error("❌ Verifique os campos destacados");
      } else if (errorData?.error?.fieldErrors) {
        // Error contract estruturado
        setFieldErrors(errorData.error.fieldErrors || {});
        setFormError(errorData.error.formError || "Erro ao criar cliente");
        toast.error(`❌ ${errorData.error.formError || "Erro ao criar cliente"}`);
      } else {
        // Erro genérico
        const message = error?.message || "Não foi possível salvar o cliente. Tente novamente.";
        setFormError(message);
        toast.error(`❌ ${message}`);
      }
    },
  });

  const updateClient = trpc.clients.update.useMutation({
    onSuccess: (data) => {
      if (data?.ok) {
        toast.success("✅ Cliente atualizado com sucesso!");
        setIsDialogOpen(false);
        setIsEditMode(false);
        setEditingClientId(null);
        resetForm();
        refetch();
      }
    },
    onError: (error: any) => {
      const errorData = error?.data?.zodError || error?.data;
      
      if (Array.isArray(errorData)) {
        const errors: FieldErrors = {};
        errorData.forEach((issue: any) => {
          const field = issue.path?.[0] || "form";
          errors[field] = issue.message || "Erro de validação";
        });
        setFieldErrors(errors);
        setFormError("Revise os campos destacados para continuar.");
        toast.error("❌ Verifique os campos destacados");
      } else if (errorData?.error?.fieldErrors) {
        setFieldErrors(errorData.error.fieldErrors || {});
        setFormError(errorData.error.formError || "Erro ao atualizar cliente");
        toast.error(`❌ ${errorData.error.formError || "Erro ao atualizar cliente"}`);
      } else {
        const message = error?.message || "Não foi possível salvar o cliente. Tente novamente.";
        setFormError(message);
        toast.error(`❌ ${message}`);
      }
    },
  });

  const deleteClient = trpc.clients.delete.useMutation({
    onSuccess: () => {
      toast.success("✅ Cliente desativado com sucesso.");
      refetch();
    },
    onError: (error: any) => {
      const message = error?.message || "Não foi possível desativar o cliente. Tente novamente.";
      toast.error(`❌ ${message}`);
    },
  });

  const updateStatus = trpc.clients.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("✅ Status atualizado com sucesso.");
      refetch();
    },
    onError: (error: any) => {
      const message = error?.message || "Não foi possível atualizar o status. Tente novamente.";
      toast.error(`❌ ${message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      channelId: isChannelAdmin && (user as any)?.channelId ? (user as any).channelId : 0,
      cnpj: "",
      razaoSocial: "",
      nomeFantasia: "",
      cnae: "",
      atividadePrincipal: "",
      grauRisco: "",
      sector: "",
      employeesCount: 0,
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
      email: "",
      phone: "",
      responsibleName: "",
    });
    setFieldErrors({});
    setFormError("");
    setIsEditMode(false);
    setEditingClientId(null);
    setActiveTab('overview');
  };

  const handleOpenDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEditClient = (client: any) => {
    setActiveTab('overview');
    setFormData({
      channelId: client.channelId || 0,
      cnpj: maskCNPJ(client.cnpj || ""),
      razaoSocial: client.razaoSocial || "",
      nomeFantasia: client.nomeFantasia || "",
      cnae: client.cnae || "",
      atividadePrincipal: client.atividadePrincipal || "",
      grauRisco: client.grauRisco || client.riskGrade || "",
      sector: client.sector || "",
      employeesCount: client.employeesCount || 0,
      logradouro: client.logradouro || "",
      numero: client.numero || "",
      complemento: client.complemento || "",
      bairro: client.bairro || "",
      cidade: client.cidade || "",
      estado: client.estado || "",
      cep: maskCEP(client.cep || ""),
      email: client.email || "",
      phone: maskPhone(client.phone || ""),
      responsibleName: client.responsibleName || "",
    });
    setEditingClientId(client.id);
    setIsEditMode(true);
    setFieldErrors({});
    setFormError("");
    setIsDialogOpen(true);
  };

  const handleInputChange = (field: string, value: string | number) => {
    // Aplicar máscaras conforme o campo
    let maskedValue: string | number = value;
    
    if (field === "cnpj" && typeof value === "string") {
      maskedValue = maskCNPJ(value);
    } else if (field === "cep" && typeof value === "string") {
      maskedValue = maskCEP(value);
    } else if (field === "phone" && typeof value === "string") {
      maskedValue = maskPhone(value);
    } else if (field === "cnae" && typeof value === "string") {
      maskedValue = maskCNAE(value);
    } else if (field === "estado" && typeof value === "string") {
      maskedValue = value.toUpperCase().slice(0, 2);
    }
    
    setFormData(prev => ({ ...prev, [field]: maskedValue }));
    
    // Limpar erro do campo ao editar
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldErrors({});
    setFormError("");

    try {
      // Limpar máscaras antes de validar
      const cleanData = {
        ...formData,
        cnpj: (formData.cnpj as string)?.replace(/\D/g, ""),
        cep: (formData.cep as string)?.replace(/\D/g, ""),
        phone: (formData.phone as string)?.replace(/\D/g, ""),
        cnae: (formData.cnae as string)?.replace(/\D/g, ""),
      };

      // Validar com Zod (frontend)
      const validated = clientFormSchema.parse(cleanData);

      // Enviar para API
      if (isEditMode && editingClientId) {
        await updateClient.mutateAsync({
          id: editingClientId,
          ...validated,
        });
      } else {
        await createClient.mutateAsync(validated);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Mapear erros de validação Zod
        const errors: FieldErrors = {};
        if (error.issues && Array.isArray(error.issues)) {
          error.issues.forEach((err: any) => {
            const field = err.path[0] as string;
            errors[field] = err.message;
          });
        }
        setFieldErrors(errors);
        setFormError("Revise os campos destacados para continuar.");
        toast.error("❌ Verifique os campos destacados");
      } else if (error instanceof Error) {
        setFormError(error.message);
        toast.error(`❌ ${error.message}`);
      } else {
        setFormError("Erro desconhecido ao salvar cliente.");
        toast.error("❌ Erro ao salvar cliente. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = (clientId: number, clientName: string) => {
    if (confirm(`Desativar cliente "${clientName}"?`)) {
      deleteClient.mutate({ id: clientId });
    }
  };

  const handleReactivateClient = (clientId: number, clientName: string) => {
    if (confirm(`Reativar cliente "${clientName}"?`)) {
      updateStatus.mutate({ id: clientId, status: "active" });
    }
  };

  return (
    <SalviaLayout>
      <div className="space-y-6">
        {/* Dev Banner */}
        <DevBanner
          simulatedIsMatriz={simulatedIsMatriz}
          onToggleSimulatedMatriz={setSimulatedIsMatriz}
          onResetState={() => {
            setActiveTab('overview');
            setSimulatedIsMatriz(false);
            resetForm();
          }}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
            <p className="text-gray-600 mt-1">Gerencie os clientes do seu canal</p>
          </div>
          <Button onClick={handleOpenDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Cliente
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, razão social ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filtro de Status */}
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro de Canal (apenas Admin Salvia) */}
            {isAdminSalvia && (
              <Select value={selectedChannelId} onValueChange={setSelectedChannelId}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por canal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Todos os canais</SelectItem>
                  {channels?.map(channel => (
                    <SelectItem key={channel.id} value={channel.id.toString()}>
                      {channel.nomeFantasia || channel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Botão de Atualizar */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Lista de Clientes */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-600">Nenhum cliente encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Nome Fantasia</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">CNPJ</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Razão Social</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map(client => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {client.nomeFantasia || client.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {maskCNPJ(client.cnpj || "")}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {client.razaoSocial}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          client.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {client.status === "active" ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClient(client)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {client.status === "active" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClient(client.id, client.nomeFantasia || client.name)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReactivateClient(client.id, client.nomeFantasia || client.name)}
                          >
                            <RefreshCw className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de Criar/Editar Cliente */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto">
          <DialogHeader>
            <div className="space-y-2">
              <DialogTitle>
                {isEditMode ? "Editar Cliente" : "Novo Cliente"}
                <span className="text-xs text-gray-500 ml-2">(UI v2 - Tabs)</span>
              </DialogTitle>
              {isEditMode && formData && (
                <CompanyStatusBadges company={formData} />
              )}
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="structure" disabled={!isEditMode}>
                Matriz e Filiais
              </TabsTrigger>
              <TabsTrigger value="group" disabled={!isEditMode}>
                Grupo Econômico
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <form onSubmit={handleSubmit} className="space-y-6">
            {/* Alerta de Erro Geral */}
            {formError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{formError}</AlertDescription>
              </Alert>
            )}

            {/* Seção: Dados Básicos */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Dados Básicos</h3>

              {/* Canal (apenas Admin Salvia) */}
              {isAdminSalvia && !isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Canal *
                  </label>
                  <Select 
                    value={formData.channelId?.toString() || "0"} 
                    onValueChange={(value) => handleInputChange("channelId", parseInt(value))}
                  >
                    <SelectTrigger className={fieldErrors.channelId ? "border-red-500" : ""}>
                      <SelectValue placeholder="Selecione um canal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Selecione um canal</SelectItem>
                      {channels?.map(channel => (
                        <SelectItem key={channel.id} value={channel.id.toString()}>
                          {channel.nomeFantasia || channel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.channelId && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.channelId}</p>
                  )}
                </div>
              )}

              {/* Nome Fantasia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Fantasia *
                </label>
                <Input
                  placeholder="Ex: Empresa XYZ"
                  value={formData.nomeFantasia || ""}
                  onChange={(e) => handleInputChange("nomeFantasia", e.target.value)}
                  className={fieldErrors.nomeFantasia ? "border-red-500" : ""}
                />
                {fieldErrors.nomeFantasia && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.nomeFantasia}</p>
                )}
              </div>

              {/* Razão Social */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razão Social *
                </label>
                <Input
                  placeholder="Ex: Empresa XYZ Ltda"
                  value={formData.razaoSocial || ""}
                  onChange={(e) => handleInputChange("razaoSocial", e.target.value)}
                  className={fieldErrors.razaoSocial ? "border-red-500" : ""}
                />
                {fieldErrors.razaoSocial && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.razaoSocial}</p>
                )}
              </div>

              {/* CNPJ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNPJ *
                </label>
                <Input
                  placeholder="XX.XXX.XXX/XXXX-XX"
                  value={formData.cnpj || ""}
                  onChange={(e) => handleInputChange("cnpj", e.target.value)}
                  className={fieldErrors.cnpj ? "border-red-500" : ""}
                />
                {fieldErrors.cnpj && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.cnpj}</p>
                )}
              </div>
            </div>

            {/* Seção: Endereço Completo (sempre aberto) */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Endereço Completo</h3>

              {/* Logradouro */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logradouro *
                </label>
                <Input
                  placeholder="Ex: Rua das Flores"
                  value={formData.logradouro || ""}
                  onChange={(e) => handleInputChange("logradouro", e.target.value)}
                  className={fieldErrors.logradouro ? "border-red-500" : ""}
                />
                {fieldErrors.logradouro && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.logradouro}</p>
                )}
              </div>

              {/* Número e Complemento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número *
                  </label>
                  <Input
                    placeholder="Ex: 123"
                    value={formData.numero || ""}
                    onChange={(e) => handleInputChange("numero", e.target.value)}
                    className={fieldErrors.numero ? "border-red-500" : ""}
                  />
                  {fieldErrors.numero && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.numero}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <Input
                    placeholder="Ex: Apto 101"
                    value={formData.complemento || ""}
                    onChange={(e) => handleInputChange("complemento", e.target.value)}
                  />
                </div>
              </div>

              {/* Bairro e Cidade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bairro *
                  </label>
                  <Input
                    placeholder="Ex: Centro"
                    value={formData.bairro || ""}
                    onChange={(e) => handleInputChange("bairro", e.target.value)}
                    className={fieldErrors.bairro ? "border-red-500" : ""}
                  />
                  {fieldErrors.bairro && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.bairro}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade *
                  </label>
                  <Input
                    placeholder="Ex: São Paulo"
                    value={formData.cidade || ""}
                    onChange={(e) => handleInputChange("cidade", e.target.value)}
                    className={fieldErrors.cidade ? "border-red-500" : ""}
                  />
                  {fieldErrors.cidade && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.cidade}</p>
                  )}
                </div>
              </div>

              {/* Estado e CEP */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <Select 
                    value={formData.estado || ""} 
                    onValueChange={(value) => handleInputChange("estado", value)}
                  >
                    <SelectTrigger className={fieldErrors.estado ? "border-red-500" : ""}>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS_BRASIL.map(estado => (
                        <SelectItem key={estado} value={estado}>
                          {estado}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.estado && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.estado}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP *
                  </label>
                  <Input
                    placeholder="XXXXX-XXX"
                    value={formData.cep || ""}
                    onChange={(e) => handleInputChange("cep", e.target.value)}
                    className={fieldErrors.cep ? "border-red-500" : ""}
                  />
                  {fieldErrors.cep && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.cep}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Seção: Dados NR1 */}
            <div className="space-y-4 border-b pb-4">
              <h3 className="font-semibold text-gray-900">Dados NR1</h3>

              {/* CNAE */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNAE
                </label>
                <Input
                  placeholder="XXXXXXX"
                  value={formData.cnae || ""}
                  onChange={(e) => handleInputChange("cnae", e.target.value)}
                  className={fieldErrors.cnae ? "border-red-500" : ""}
                />
                {fieldErrors.cnae && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.cnae}</p>
                )}
              </div>

              {/* Grau de Risco */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grau de Risco
                </label>
                <Select 
                  value={formData.grauRisco || ""} 
                  onValueChange={(value) => handleInputChange("grauRisco", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRAUS_RISCO.map(grau => (
                      <SelectItem key={grau.value} value={grau.value}>
                        {grau.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Atividade Principal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Atividade Principal
                </label>
                <Input
                  placeholder="Ex: Comércio"
                  value={formData.atividadePrincipal || ""}
                  onChange={(e) => handleInputChange("atividadePrincipal", e.target.value)}
                />
              </div>

              {/* Setor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setor
                </label>
                <Input
                  placeholder="Ex: Varejo"
                  value={formData.sector || ""}
                  onChange={(e) => handleInputChange("sector", e.target.value)}
                />
              </div>

              {/* Número de Funcionários */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Funcionários
                </label>
                <Input
                  type="number"
                  placeholder="Ex: 50"
                  value={formData.employeesCount || ""}
                  onChange={(e) => handleInputChange("employeesCount", parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* Seção: Contato */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Contato</h3>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <Input
                  type="email"
                  placeholder="contato@empresa.com.br"
                  value={formData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={fieldErrors.email ? "border-red-500" : ""}
                />
                {fieldErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <Input
                  placeholder="(XX) XXXXX-XXXX"
                  value={formData.phone || ""}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className={fieldErrors.phone ? "border-red-500" : ""}
                />
                {fieldErrors.phone && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.phone}</p>
                )}
              </div>

              {/* Responsável */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável
                </label>
                <Input
                  placeholder="Nome do responsável"
                  value={formData.responsibleName || ""}
                  onChange={(e) => handleInputChange("responsibleName", e.target.value)}
                  className={fieldErrors.responsibleName ? "border-red-500" : ""}
                />
                {fieldErrors.responsibleName && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.responsibleName}</p>
                )}
              </div>
            </div>

                {/* Botões de Ação */}
                <div className="flex justify-end gap-3 border-t pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || createClient.isPending || updateClient.isPending}
                    className="gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        {isEditMode ? "Atualizar" : "Criar"} Cliente
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="structure" className="mt-4">
              {isEditMode ? (
                <div className="space-y-4">
                  {/* Simular Matriz para testes */}
                  <StructureTab
                    company={{
                      ...formData,
                      isMatriz: simulatedIsMatriz || formData.isMatriz,
                    }}
                  />
                  <CreateFilialDrawer
                    company={{
                      ...formData,
                      isMatriz: simulatedIsMatriz || formData.isMatriz,
                    }}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Salve o cliente primeiro para acessar a aba Matriz e Filiais.
                </p>
              )}
            </TabsContent>

            <TabsContent value="group" className="mt-4">
              {isEditMode ? (
                <GrupoEconomicoTab
                  companyId={editingClientId || undefined}
                  isControladora={formData.isControladora}
                  controladoraId={formData.controladoraId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Salve o cliente primeiro para acessar a aba Grupo Econômico.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </SalviaLayout>
  );
}
